'use client'

import React from 'react'
import { cn, formatPrice } from '@/lib/utils'

import { Hint, RibbonCard, ScrollArea } from '@/components/ui'
import { CartItem } from '@/components/molecules'
import { CartPlaceholder } from '@/components/placeholders'

interface CartPreviewProps extends React.ComponentPropsWithoutRef<'div'> {
  customer?: Customer | null
  cartId?: string
  cart?: any
}

export const CartPreview = ({
  className,
  customer,
  cartId,
  cart,
  ...restProps
}: CartPreviewProps) => {
  const totals = cart?.cart_totals?.map((line: any) => {
    if (line?.type === 'final_payble_amount' || line?.type === 'total') return
    return {
      label: line?.total_name,
      value: formatPrice(line?.amount, 2, false),
    }
  })

  const totalPayable = cart?.cart_totals?.find(
    (line: any) => line?.type === 'final_payble_amount',
  )?.amount

  return (
    <div
      className={cn(
        'flex min-h-full w-full flex-col gap-3 py-1 lg:h-full lg:gap-5',
        className,
      )}
      {...restProps}
    >
      <div className={cn('flex flex-grow gap-6 lg:overflow-hidden')}>
        <div
          className={cn(
            'flex w-full max-w-[var(--cart-products-width)] shrink-0 flex-col gap-5 lg:h-full',
          )}
        >
          <div className="flex w-full flex-col">
            <h2 className="text-base font-medium">Your Order</h2>
            <Hint>{cart?.cart_items?.length} Items in the cart</Hint>
          </div>

          {cart?.cart_items?.length === 0 && (
            <div className="flex items-center justify-center rounded-lg bg-gray-50 lg:flex-grow">
              <CartPlaceholder
                title="No Products"
                description="Your products will appear here"
              />
            </div>
          )}

          {cart?.cart_items?.length > 0 && (
            <React.Fragment>
              <ScrollArea
                className={cn(
                  'flex flex-grow flex-col lg:-mr-4 lg:pr-4',
                  className,
                )}
              >
                <div className={cn('flex w-full flex-col gap-2')}>
                  {cart?.cart_items?.map((product: any) => (
                    <CartItem
                      product={product}
                      key={product?.id}
                      onClick={() => {}}
                      className="pointer-events-none select-none"
                      isOnPreview={true}
                    />
                  ))}
                </div>
              </ScrollArea>
              <RibbonCard
                className="bg-gray-50"
                ribbonClassName="after:bg-white before:bg-white"
                listClassName="text-gray-950"
                highlightsClassName="text-sm"
                list={totals}
                highlightList={[
                  {
                    label: 'Total Payable',
                    value: formatPrice(totalPayable, 2, false),
                  },
                ]}
              />
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  )
}
